import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import type { FormManagementCompletionPage as CompletionPageContract } from '@/contracts/form-management'
import type { FormManagementWorkspaceData } from '@/contracts/form-management'
import { ArrowRight, Check, PartyPopper, Share2, TimerReset } from 'lucide-react'

type FormManagementCompletionPageProps = {
  workspaceData: FormManagementWorkspaceData
}

const animationLabels: Record<CompletionPageContract['animationStyle'], string> = {
  check_confetti: '勾勾與撒花',
  spotlight: '聚光燈',
  card_stack: '卡片堆疊',
  minimal: '極簡淡入',
}

export function FormManagementCompletionPage({ workspaceData }: FormManagementCompletionPageProps) {
  const completionPage = workspaceData.completionPage

  return (
    <div className="space-y-5">
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <Card>
          <CardContent className="p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <PartyPopper className="size-5 text-indigo-600" />
                  <h3 className="font-semibold text-slate-950">完成頁</h3>
                </div>
              </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div className="rounded-md border border-slate-200 bg-white p-4">
                <div className="text-xs font-medium text-slate-500">完成標題</div>
                <div className="mt-2 text-lg font-semibold text-slate-950">{completionPage.title}</div>
              </div>
              <div className="rounded-md border border-slate-200 bg-white p-4">
                <div className="text-xs font-medium text-slate-500">動畫效果</div>
                <div className="mt-2 text-lg font-semibold text-slate-950">
                  {animationLabels[completionPage.animationStyle]}
                </div>
              </div>
              <div className="rounded-md border border-slate-200 bg-white p-4 md:col-span-2">
                <div className="text-xs font-medium text-slate-500">完成描述</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">{completionPage.description}</p>
              </div>
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {completionPage.ctas.map((cta) => (
                <div key={cta.id} className="rounded-md bg-slate-50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-slate-900">{cta.label}</span>
                    <Badge variant={cta.style === 'primary' ? 'default' : 'secondary'}>{cta.style}</Badge>
                  </div>
                  <div className="mt-2 break-all font-mono text-xs text-slate-500">{cta.url}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-950">設定摘要</h3>
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <TimerReset className="size-4 text-indigo-600" />
                  延遲導頁
                </div>
                <Badge variant={completionPage.redirectUrl ? 'success' : 'secondary'}>
                  {completionPage.redirectUrl ? `${completionPage.redirectDelaySeconds ?? 0} 秒` : '未設定'}
                </Badge>
              </div>
              <div className="rounded-md bg-slate-50 px-3 py-2">
                <div className="text-sm font-medium text-slate-700">導頁 URL</div>
                <div className="mt-1 break-all font-mono text-xs text-slate-500">
                  {completionPage.redirectUrl ?? '未設定'}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <ArrowRight className="size-4 text-indigo-600" />
                  允許重新填寫
                </div>
                <Switch
                  aria-label="允許重新填寫"
                  checked={completionPage.showResubmitButton}
                  disabled
                  title="允許重新填寫"
                />
              </div>
              <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <TimerReset className="size-4 text-indigo-600" />
                  再次填寫新問卷
                </div>
                <Switch
                  aria-label="再次填寫新問卷"
                  checked={completionPage.showNewSubmissionButton}
                  disabled
                  title="再次填寫新問卷"
                />
              </div>
              <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <Share2 className="size-4 text-indigo-600" />
                  分享按鈕
                </div>
                <Switch
                  aria-label="分享按鈕"
                  checked={completionPage.showShareButtons}
                  disabled
                  title="分享按鈕"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="mx-auto flex max-w-2xl flex-col items-center text-center">
            <div className="flex size-14 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
              <Check className="size-7" />
            </div>
            <h3 className="mt-5 text-2xl font-semibold text-slate-950">{completionPage.title}</h3>
            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-500">{completionPage.description}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {completionPage.showNewSubmissionButton && (
                <Button type="button">
                  {completionPage.newSubmissionButtonLabel}
                </Button>
              )}
              {completionPage.ctas.map((cta) => (
                <Button key={cta.id} type="button" variant={cta.style === 'primary' ? 'default' : 'outline'}>
                  {cta.label}
                </Button>
              ))}
            </div>
            {completionPage.showShareButtons && (
              <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs text-slate-500">
                <span className="rounded-md bg-slate-100 px-2 py-1">複製連結</span>
                <span className="rounded-md bg-slate-100 px-2 py-1">Email</span>
                <span className="rounded-md bg-slate-100 px-2 py-1">社群分享</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
